import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Chip,
  FormControlLabel,
  Switch,
  CircularProgress,
  Tooltip,
  Link,
  Grid,
  Snackbar
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ContentCopy as ContentCopyIcon,
  Help as HelpIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  FilterListOff as FilterListOffIcon
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { Client } from "@microsoft/microsoft-graph-client";

// Define interfaces
interface ExtensionAttribute {
  name: string;
  isOnPremisesSynced: boolean;
  dataType?: string;
  inUse?: boolean;
  sample?: string;
}

interface CustomExtensionAttribute {
  name: string;
  type: string;
  source: string;
  inUse: boolean;
  description?: string;
  sample?: string;
}

interface ExtensionAttributesTabProps {
  getGraphClient: () => Promise<Client>;
  copyToClipboard: (text: string) => void;
  setErrorMessage: (message: string) => void;
  setLoading: (loading: boolean) => void;
  loading: boolean;
}

const ExtensionAttributesTab: React.FC<ExtensionAttributesTabProps> = ({
  getGraphClient,
  copyToClipboard,
  setErrorMessage,
  setLoading,
  loading
}) => {
  // State variables
  const [builtInAttributes, setBuiltInAttributes] = useState<ExtensionAttribute[]>([]);
  const [customAttributes, setCustomAttributes] = useState<CustomExtensionAttribute[]>([]);
  const [showAllExtAttributes, setShowAllExtAttributes] = useState<boolean>(false);
  const [showUnusedAttributes, setShowUnusedAttributes] = useState<boolean>(false);
  const [isScanning, setIsScanning] = useState<boolean>(false);

  // Add these states if they don't exist
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "info" | "warning" | "error">("info");
  
  // Initialize built-in extension attributes
  useEffect(() => {
    const attributes: ExtensionAttribute[] = [];
    for (let i = 1; i <= 15; i++) {
      attributes.push({
        name: `extensionAttribute${i}`,
        isOnPremisesSynced: false,
        dataType: "String",
        inUse: false
      });
    }
    setBuiltInAttributes(attributes);
  }, []);

  // Scan for extension attribute usage
  const scanExtensionAttributeUsage = useCallback(async () => {
    setIsScanning(true);
    setErrorMessage("");

    try {
      setLoading(true);
      const client = await getGraphClient();

      // --- Scan a sample of users for attribute values ---
      const selectFields = [
        'id', 'displayName', 'mail',
        ...Array.from({ length: 15 }, (_, i) => `extensionAttribute${i + 1}`)
        // Note: We cannot reliably select *all* potential custom extension attributes
        // like extension_appid_customAttr here, as we don't know them beforehand.
        // We rely on the user objects returning populated custom attributes if they exist.
      ].join(',');

      console.log(`Scanning user sample for attribute values with fields: ${selectFields}`);
      const userResponse = await client.api('/users')
        .select(selectFields)
        // Requesting $select=* might return custom attributes, but is less efficient.
        // Relying on default properties + explicitly selected ones is usually better.
        // Graph might return populated custom extensions even if not explicitly selected,
        // but it's not guaranteed for all scenarios.
        .top(50) // Increased sample size slightly for better chance of finding values
        .get();

      const usageMap: Record<string, { hasValue: boolean, sample?: string }> = {};
      let foundAnyValue = false;

      if (userResponse.value && userResponse.value.length > 0) {
        console.log(`Retrieved ${userResponse.value.length} users to analyze for attribute values`);
        userResponse.value.forEach((user: any) => {
          // Check built-in attributes
          for (let i = 1; i <= 15; i++) {
            const attrName = `extensionAttribute${i}`;
            if (user[attrName] !== undefined && user[attrName] !== null && String(user[attrName]).trim() !== "") {
              usageMap[attrName] = { hasValue: true, sample: String(user[attrName]) };
              foundAnyValue = true;
            } else if (!usageMap[attrName]) {
              // Initialize if not seen before
              usageMap[attrName] = { hasValue: false };
            }
          }
          // Check custom attributes found on the user object
          Object.keys(user).forEach(key => {
            if (key.startsWith('extension_') && !key.startsWith('extensionAttribute')) {
               if (user[key] !== undefined && user[key] !== null && String(user[key]).trim() !== "") {
                  usageMap[key] = {
                      hasValue: true,
                      sample: typeof user[key] === 'object' ? JSON.stringify(user[key]) : String(user[key])
                  };
                  foundAnyValue = true;
               } else if (!usageMap[key]) {
                 usageMap[key] = { hasValue: false };
               }
            }
          });
        });

        // Update built-in attributes state based on *value presence* in sample
        setBuiltInAttributes(prevAttrs =>
          prevAttrs.map(attr => ({
            ...attr,
            inUse: !!usageMap[attr.name]?.hasValue, // Still based on finding a value
            sample: usageMap[attr.name]?.sample
          }))
        );

        if (!foundAnyValue) {
          console.log("No extension attribute *values* found among scanned users.");
          // Adjust snackbar message - finding definitions is separate
        } else {
          console.log("Found some extension attribute *values* in user sample.");
        }
        // Snackbar message will be updated after fetching definitions

      } else {
        console.warn("No users were returned from the query to scan for values.");
        setErrorMessage("No users found to scan for attribute values.");
        setSnackbarMessage("Could not find users to scan for values.");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
      }

      // --- Scan for custom extension definitions (schema) ---
      const customExtensions: CustomExtensionAttribute[] = [];
      let foundDefinitions = false;
      try {
        const schemaAppFilter = "startswith(displayName, 'Tenant Schema Extension') or startswith(displayName, 'AAD Schema') or startswith(displayName, 'Azure AD Schema')";
        const appsResponse = await client.api('/applications')
          .filter(schemaAppFilter)
          .select('id,appId,displayName')
          .top(10)
          .get();

        const knownSchemaAppIds = new Set<string>();
        if (appsResponse.value) {
            appsResponse.value.forEach((app: { id: string }) => knownSchemaAppIds.add(app.id));
        }

        if (appsResponse.value && appsResponse.value.length > 0) {
          console.log(`Found ${appsResponse.value.length} potential schema extension app(s).`);
          for (const app of appsResponse.value) {
            try {
              const extResponse = await client.api(`/applications/${app.id}/extensionProperties`)
                .select('name,dataType,targetObjects')
                .get();

              if (extResponse.value && extResponse.value.length > 0) {
                extResponse.value.forEach((ext: any) => {
                  // First get the full name as it appears in the API response
                  const fullExtName = ext.name;
                  
                  // Also extract what might be just the property name (if already prefixed)
                  const propertyNameMatch = fullExtName.match(/extension_[a-f0-9]+_(.+)$/);
                  const propertyNameOnly = propertyNameMatch ? propertyNameMatch[1] : fullExtName;
                  
                  // Now check if either the full name or property part matches extensionAttribute pattern
                  const isExtensionAttribute = 
                    // Either the full name contains extensionAttribute
                    fullExtName.includes('extensionAttribute') ||
                    // Or the property part is extensionAttribute1-15
                    /^extensionAttribute(\d{1,2})$/.test(propertyNameOnly);
                  
                  // Additional check for valid numbers 1-15 if it's an extensionAttribute
                  let isValidExtAttrNumber = true;
                  if (isExtensionAttribute) {
                    // Extract the number from extensionAttribute{N}
                    const numberMatch = propertyNameOnly.match(/extensionAttribute(\d{1,2})$/);
                    if (numberMatch) {
                      const number = parseInt(numberMatch[1], 10);
                      isValidExtAttrNumber = number >= 1 && number <= 15;
                    }
                  }
                  
                  // If it's a valid extension attribute 1-15, add it to our list
                  if (isExtensionAttribute && isValidExtAttrNumber) {
                    // Ensure the name is correctly formed with app prefix if needed
                    let extName = fullExtName;
                    // If it doesn't already have the extension_ prefix, add it
                    if (!extName.startsWith('extension_')) {
                      const expectedPrefix = `extension_${app.appId.replace(/-/g, '')}_`;
                      extName = expectedPrefix + extName;
                    }
                
                    // Mark as in use since it's defined on a schema app
                    const isFromKnownSchemaApp = knownSchemaAppIds.has(app.id);
                    const isInUse = isFromKnownSchemaApp;
                
                    console.log(`Found valid extension attribute: ${extName}`);
                    
                    customExtensions.push({
                      name: extName,
                      type: ext.dataType || "String",
                      source: app.displayName || app.id,
                      inUse: isInUse,
                      sample: usageMap[extName]?.sample,
                      description: `Synced from On-Premises AD (Target: ${ext.targetObjects?.join(', ') || 'User'})`
                    });
                    foundDefinitions = true;
                  }
                });
              }
            } catch (appExtError: any) {
              console.error(`Error fetching extensions for app ${app.displayName || app.id}:`, appExtError.message || appExtError);
            }
          }
        } else {
            console.log("No applications found matching the schema app filter.");
        }

        // Sort custom attributes alphabetically by name
        customExtensions.sort((a, b) => a.name.localeCompare(b.name));
        setCustomAttributes(customExtensions); // Set the filtered list

        // Update Snackbar based on finding relevant definitions
        if (foundDefinitions) {
            setSnackbarMessage("Scan complete. Found synced extensionAttribute(s) 1-15.");
            setSnackbarSeverity("success");
        } else if (!foundAnyValue && !foundDefinitions) { // Adjusted condition
            setSnackbarMessage("Scan complete. No extension attributes found in use or defined.");
            setSnackbarSeverity("info");
        } else {
             setSnackbarMessage("Scan complete. No synced extensionAttribute(s) 1-15 found.");
             setSnackbarSeverity("warning");
        }
        setSnackbarOpen(true);


      } catch (customExtError: any) {
        console.error("Error fetching custom extension definitions:", customExtError);
        const errorDetail = customExtError.message || 'Unknown error';
        setErrorMessage(`Failed to fetch custom extension definitions: ${errorDetail}`);
        setSnackbarMessage("Error fetching custom extension definitions.");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
      }

    } catch (error: any) { // Catch block for the main try (errors during user scan mostly)
      console.error("Error during extension attribute scan:", error);
      let errorMsg = "Failed to scan extension attributes";
      if (error.message) {
        errorMsg += `: ${error.message}`;
      } else if (error.body?.error?.message) {
        errorMsg += `: ${error.body.error.message}`;
      }
      setErrorMessage(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);

    } finally {
      setLoading(false);
      setIsScanning(false);
    }
  }, [getGraphClient, setErrorMessage, setLoading, setBuiltInAttributes, setCustomAttributes, setSnackbarOpen, setSnackbarMessage, setSnackbarSeverity]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Extension Attributes
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showUnusedAttributes}
                onChange={(e) => setShowUnusedAttributes(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                Show Unused Attributes
              </Typography>
            }
          />
          
          <Button
            variant="contained"
            color="primary"
            onClick={scanExtensionAttributeUsage}
            disabled={isScanning}
            startIcon={isScanning ? <CircularProgress size={20} /> : <SearchIcon />}
            sx={{ borderRadius: 2 }}
          >
            {isScanning ? "Scanning..." : "Scan Extension Attributes"}
          </Button>
        </Box>
      </Box>
      
      <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="body1" paragraph>
          Entra ID includes 15 built-in extension attributes that can be used for storing custom data.
          Additionally, custom extension attributes can be created through Entra Connect or programmatically.
        </Typography>
      </Paper>
      
      {/* Built-in Extension Attributes Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          borderRadius: 2, 
          mb: 3,
          border: '1px solid',
          borderColor: theme => theme.palette.primary.light,
          backgroundColor: alpha('#f5f9ff', 0.7) 
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 2 
        }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Built-in Extension Attributes
          </Typography>
          
          <Button
            variant="outlined"
            size="small"
            sx={{ borderRadius: 2 }}
            endIcon={<ExpandMoreIcon />}
            onClick={() => setShowAllExtAttributes(!showAllExtAttributes)}
          >
            {showAllExtAttributes ? "Show Less" : "Show All Attributes"}
          </Button>
        </Box>
        
        <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: alpha('#f5f9ff', 0.7) }}>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Sample Value</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {builtInAttributes
                .filter((attr, index) => showAllExtAttributes || index < 5)
                .filter(attr => showUnusedAttributes || attr.inUse)
                .map((attr) => (
                <TableRow key={attr.name} hover>
                  <TableCell>
                    <Typography sx={{ fontWeight: 500 }}>{attr.name}</Typography>
                  </TableCell>
                  <TableCell>{attr.dataType || "String"}</TableCell>
                  <TableCell>
                    {attr.inUse ? (
                      <Chip size="small" label="In Use" color="success" />
                    ) : (
                      <Chip size="small" label="Not Used" color="default" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={attr.sample || "No sample available"}>
                      <Typography 
                        sx={{ 
                          maxWidth: '200px', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap' 
                        }}
                      >
                        {attr.sample || "-"}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      size="small" 
                      onClick={() => copyToClipboard(`{\n  "Source": "User",\n  "ID": "${attr.name}",\n  "JwtClaimType": "extension_attribute"\n}`)}
                      title="Copy claims mapping format"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {builtInAttributes.some(attr => attr.inUse) && (
          <Alert severity="success" sx={{ mt: 2 }}>
            <AlertTitle>Extension Attributes In Use</AlertTitle>
            Your tenant is using built-in extension attributes, which can be included in tokens via claims mapping policies.
          </Alert>
        )}
      </Paper>
      
      {/* Custom Extension Attributes Section */}
      {customAttributes.length > 0 && (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            borderRadius: 2, 
            mb: 3,
            border: '1px solid',
            borderColor: theme => theme.palette.primary.light,
            backgroundColor: alpha('#f5f9ff', 0.7) 
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Custom Extension Attributes
          </Typography>
          
          <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha('#f5f9ff', 0.7) }}>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Sample Value</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customAttributes
                  .filter(attr => showUnusedAttributes || attr.inUse)
                  .map((attr) => (
                  <TableRow key={attr.name} hover>
                    <TableCell>
                      <Tooltip title={attr.name}>
                        <Typography 
                          sx={{ 
                            fontWeight: 500, 
                            maxWidth: '200px', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap' 
                          }}
                        >
                          {attr.name}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{attr.type}</TableCell>
                    <TableCell>
                      <Chip size="small" label={attr.source} />
                    </TableCell>
                    <TableCell>
                      {attr.inUse ? (
                        <Chip size="small" label="In Use" color="success" />
                      ) : (
                        <Chip size="small" label="Not Used" color="default" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={attr.sample || "No sample available"}>
                        <Typography 
                          sx={{ 
                            maxWidth: '150px', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap' 
                          }}
                        >
                          {attr.sample || "-"}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        size="small" 
                        onClick={() => copyToClipboard(`{\n  "Source": "User",\n  "ID": "${attr.name}",\n  "JwtClaimType": "extension_attribute"\n}`)}
                        title="Copy claims mapping format"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <AlertTitle>About Custom Extension Attributes</AlertTitle>
            <Typography variant="body2">
              Custom extension attributes are created through Entra Connect or programmatically via Microsoft Graph API.
              Only some custom attributes can be included in tokens via claims mapping policies.
            </Typography>
          </Alert>
        </Paper>
      )}
      
      {/* Claims Mapping Information */}
      <Alert severity="info" icon={<InfoIcon />} sx={{ borderRadius: 2, mb: 3 }}>
        <AlertTitle>Using Extension Attributes in Claims Mapping</AlertTitle>
        <Typography variant="body2" paragraph>
          Extension attributes can be included in tokens via claims mapping policies.
        </Typography>
        
        <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 600 }}>
          Claims Mapping Format Example:
        </Typography>
        
        <Box 
          sx={{ 
            backgroundColor: 'background.paper', 
            p: 2, 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            overflow: 'auto',
            maxWidth: '100%',
            mb: 2
          }}
        >
          <pre style={{ margin: 0 }}>
        {`{
  "source": "user",
  "ID": "extensionAttribute1",
  "JwtClaimType": "extension_attribute1"
}`}
          </pre>
        </Box>
        
        <Button
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          onClick={() => copyToClipboard(
            `{\n  "Source": "User",\n  "ID": "extensionAttribute1",\n  "JwtClaimType": "extension_Attribute1"\n}`
          )}
          sx={{ borderRadius: 2 }}
          size="small"
        >
          Copy Format
        </Button>
      </Alert>
      
      {/* Help Information - Keep the existing accordions */}
      <Box sx={{ mt: 4 }}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <HelpIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Working with Extension Attributes
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Setting Extension Attributes in Entra ID
            </Typography>
            
            <Typography paragraph>
              Extension attributes can be managed directly in Entra ID through:
            </Typography>
            
            <ul>
              <li>Microsoft Graph API</li>
              <li>PowerShell (Set-AzureADUser -ObjectId user-id -ExtensionProperty @&#123;"extensionAttribute1"="value"&#125;)</li>
              <li>Custom applications that integrate with Microsoft Graph</li>
              <li>Azure Automation or other automation tools</li>
            </ul>
            
            <Typography paragraph mt={2}>
              If you're using Entra Connect, these attributes can also be synchronized from on-premises AD.
            </Typography>
            
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
              Common Uses for Extension Attributes
            </Typography>
            
            <ul>
              <li>Employee types or classifications</li>
              <li>Cost centers or accounting codes</li>
              <li>Location or office codes</li>
              <li>Custom role identifiers</li>
              <li>License or entitlement information</li>
              <li>User preferences or settings</li>
              <li>Application-specific data that should be included in tokens</li>
            </ul>
          </AccordionDetails>
        </Accordion>
        
        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Microsoft Graph API Examples
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Get user extension attributes
            </Typography>
            
            <Box 
              sx={{ 
                backgroundColor: alpha('#000', 0.03), 
                p: 2, 
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                mb: 2
              }}
            >
              <pre style={{ margin: 0, overflowX: 'auto' }}>
                {`GET https://graph.microsoft.com/v1.0/users/{id}?$select=displayName,extensionAttribute1,extensionAttribute2`}
              </pre>
            </Box>
            
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 3 }}>
              Update extension attributes
            </Typography>
            
            <Box 
              sx={{ 
                backgroundColor: alpha('#000', 0.03), 
                p: 2, 
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.875rem'
              }}
            >
              <pre style={{ margin: 0, overflowX: 'auto' }}>
                {`PATCH https://graph.microsoft.com/v1.0/users/{id}
{
  "extensionAttribute1": "Value1",
  "extensionAttribute2": "Value2"
}`}
              </pre>
            </Box>
            
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() => copyToClipboard(
                `PATCH https://graph.microsoft.com/v1.0/users/{id}\n{\n  "extensionAttribute1": "Value1",\n  "extensionAttribute2": "Value2"\n}`
              )}
              sx={{ mt: 2, borderRadius: 2 }}
              size="small"
            >
              Copy Example
            </Button>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
    </Box>
  );
};

export default ExtensionAttributesTab;